import type { Href } from "expo-router";
import { router } from "expo-router";
import * as Clipboard from "expo-clipboard";
import { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import QRCode from "react-native-qrcode-svg";

import { isAdminRole } from "../../../../lib/auth";
import { useAuth } from "../../../../lib/authContext";
import {
  enrollTotp,
  hasVerifiedTotp,
  listFactors,
  unenrollUnverifiedTotpFactors,
  verifyEnrollment,
} from "../../../../lib/mfa";
import { supabase } from "../../../../lib/supabase";
import { FONTS } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";
import {
  AdminButton,
  AdminError,
  AdminLoading,
  AdminPageHeader,
} from "../../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

const BLACK = THEME.pageBg;
const FOREST_MID = THEME.surfaceRaisedBg;
const FOREST = THEME.sidebarBg;
const AMBER = THEME.accent;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = THEME.destructive;
const BORDER = "rgba(255,255,255,0.07)";

export default function AdminMfaEnrollScreen() {
  const { user, role, loading: authLoading, refetchProfile } = useAuth();
  const [busy, setBusy] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [otpUri, setOtpUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const goNextAfterMfa = useCallback(async () => {
    await refetchProfile();
    const uid = (await supabase.auth.getUser()).data.user?.id;
    if (!uid) {
      router.replace("/admin" as Href);
      return;
    }
    const { data } = await supabase.from("user_profiles").select("employee_status").eq("id", uid).maybeSingle();
    const es = data?.employee_status;
    if (es === "pending") router.replace("/admin/pending" as Href);
    else router.replace("/admin" as Href);
  }, [refetchProfile]);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.id || !isAdminRole(role)) {
      router.replace("/login" as Href);
      return;
    }

    let cancelled = false;
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        const data = await listFactors();
        if (cancelled) return;
        if (hasVerifiedTotp(data)) {
          await goNextAfterMfa();
          return;
        }
        await unenrollUnverifiedTotpFactors();
        if (cancelled) return;
        const u = (await supabase.auth.getUser()).data.user;
        const email = u?.email ?? "";
        const friendly = `Coravu ${email}`.trim();
        const enrolled = await enrollTotp(friendly);
        if (cancelled) return;
        setFactorId(enrolled.id);
        setOtpUri(enrolled.totp.uri);
        setSecret(enrolled.totp.secret);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Could not start MFA setup");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.id, role, goNextAfterMfa]);

  async function onVerify() {
    if (!factorId) return;
    const trimmed = code.replace(/\D/g, "").trim();
    if (trimmed.length !== 6) {
      setErr("Enter the 6-digit code from your app.");
      return;
    }
    setErr(null);
    setEnrolling(true);
    try {
      await verifyEnrollment(factorId, trimmed);
      await goNextAfterMfa();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Invalid code, try again");
    } finally {
      setEnrolling(false);
    }
  }

  if (authLoading || busy) {
    return (
      <View style={{ flex: 1, justifyContent: "center", backgroundColor: BLACK }}>
        <AdminLoading theme={THEME} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: BLACK }}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 32 }}>
        <View style={{ padding: 24, maxWidth: 640 }}>
          <AdminPageHeader
            theme={THEME}
            eyebrow="Admin · Two-factor"
            title="Set up authenticator app"
            subtitle="Scan the QR code with Microsoft Authenticator, Google Authenticator, or 1Password — then enter the 6-digit code your app shows."
          />

          {err ? <View style={{ marginBottom: 14 }}><AdminError theme={THEME} message={err} /></View> : null}

          {/* QR code panel */}
          {otpUri ? (
            <View
              style={{
                marginTop: 18,
                padding: 22,
                borderRadius: 6,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: BORDER,
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9.5,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                Scan in your authenticator app
              </Text>
              <View
                style={{
                  marginTop: 14,
                  padding: 16,
                  borderRadius: 6,
                  backgroundColor: LINEN,
                }}
                accessibilityLabel="TOTP QR code"
                accessible
              >
                <QRCode value={otpUri} size={200} />
              </View>

              {/* Manual-entry disclosure */}
              {secret ? (
                <View style={{ marginTop: 14, alignSelf: "stretch" }}>
                  <Pressable
                    onPress={() => setShowSecret((s) => !s)}
                    style={[
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text
                      style={{
                        fontFamily: FONTS.bodyMedium,
                        fontSize: 11.5,
                        color: AMBER,
                        textAlign: "center",
                      }}
                    >
                      {showSecret ? "Hide secret" : "Can't scan? Show secret"}
                    </Text>
                  </Pressable>
                  {showSecret ? (
                    <View
                      style={{
                        marginTop: 10,
                        padding: 12,
                        borderRadius: 4,
                        backgroundColor: BLACK,
                        borderWidth: 1,
                        borderColor: BORDER,
                      }}
                    >
                      <Text
                        selectable
                        style={{
                          fontFamily: Platform.OS === "web" ? ("'JetBrains Mono', Menlo, monospace" as string) : "Menlo",
                          fontSize: 13,
                          color: LINEN,
                          letterSpacing: 1.2,
                          textAlign: "center",
                        }}
                      >
                        {secret}
                      </Text>
                      <Pressable
                        onPress={() => {
                          void Clipboard.setStringAsync(secret);
                          setSecretCopied(true);
                          setTimeout(() => setSecretCopied(false), 1600);
                        }}
                        style={[
                          {
                            marginTop: 10,
                            alignSelf: "center",
                            paddingHorizontal: 12,
                            paddingVertical: 7,
                            borderRadius: 3,
                            borderWidth: 1,
                            borderColor: BORDER,
                            backgroundColor: "rgba(255,255,255,0.02)",
                          } as ViewStyle,
                          Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                        ]}
                      >
                        <Text
                          style={{
                            fontFamily: FONTS.bodyBold,
                            fontSize: 10,
                            letterSpacing: 1.2,
                            textTransform: "uppercase",
                            color: secretCopied ? AMBER : WS,
                          }}
                        >
                          {secretCopied ? "Copied" : "Copy secret"}
                        </Text>
                      </Pressable>
                    </View>
                  ) : null}
                </View>
              ) : null}
            </View>
          ) : null}

          {/* 6-digit code */}
          <View
            style={{
              marginTop: 18,
              padding: 22,
              borderRadius: 6,
              backgroundColor: FOREST_MID,
              borderWidth: 1,
              borderColor: BORDER,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Enter 6-digit code
            </Text>
            <TextInput
              value={code}
              onChangeText={(t) => setCode(t.replace(/\D/g, "").slice(0, 6))}
              keyboardType="number-pad"
              placeholder="000000"
              placeholderTextColor={WL}
              maxLength={6}
              style={
                {
                  marginTop: 12,
                  backgroundColor: BLACK,
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 4,
                  paddingHorizontal: 12,
                  paddingVertical: 14,
                  color: LINEN,
                  fontFamily: Platform.OS === "web" ? ("'JetBrains Mono', Menlo, monospace" as string) : "Menlo",
                  fontSize: 24,
                  letterSpacing: 8,
                  textAlign: "center",
                  ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
                } as unknown as TextStyle
              }
            />

            <View style={{ marginTop: 14, alignSelf: "stretch" }}>
              <AdminButton
                theme={THEME}
                label="I've added it"
                fullWidth
                loading={enrolling}
                disabled={!factorId || code.length !== 6}
                onPress={() => void onVerify()}
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

void W;
